/**
 * In-memory presigned URL cache for quest travel document previews.
 * Cleared on route change, logout, or via clearTravelDocumentPreviewUrlCache().
 */

const REFRESH_BUFFER_MS = 45 * 1000

const presignedUrlCache = new Map()

export function getAwsPresignedUrlExpiryMs(url) {
  if (!url || typeof url !== 'string') return null
  try {
    const u = new URL(url)
    const amzDate = u.searchParams.get('X-Amz-Date')
    const expiresSec = parseInt(u.searchParams.get('X-Amz-Expires'), 10)
    if (!amzDate || !Number.isFinite(expiresSec)) return null
    if (amzDate.length < 15) return null
    const year = parseInt(amzDate.slice(0, 4), 10)
    const month = parseInt(amzDate.slice(4, 6), 10) - 1
    const day = parseInt(amzDate.slice(6, 8), 10)
    const hour = parseInt(amzDate.slice(9, 11), 10)
    const minute = parseInt(amzDate.slice(11, 13), 10)
    const second = parseInt(amzDate.slice(13, 15), 10)
    const issuedMs = Date.UTC(year, month, day, hour, minute, second)
    return issuedMs + expiresSec * 1000
  } catch {
    return null
  }
}

export function isPresignedDocumentUrlFresh(url, now = Date.now()) {
  const exp = getAwsPresignedUrlExpiryMs(url)
  if (exp == null) return false
  return now < exp - REFRESH_BUFFER_MS
}

export function previewUrlCacheKey(questId, userId, fileName) {
  return `${questId}|${userId}|${fileName}`
}

export function getCachedTravelDocumentPresignedUrl(questId, userId, fileName) {
  const key = previewUrlCacheKey(questId, userId, fileName)
  const entry = presignedUrlCache.get(key)
  const url = entry?.url
  if (!url || typeof url !== 'string') return null
  if (!isPresignedDocumentUrlFresh(url)) {
    presignedUrlCache.delete(key)
    return null
  }
  return url
}

export function setCachedTravelDocumentPresignedUrl(
  questId,
  userId,
  fileName,
  url,
) {
  if (!url || typeof url !== 'string') return
  presignedUrlCache.set(previewUrlCacheKey(questId, userId, fileName), { url })
}

export function invalidateCachedTravelDocumentPresignedUrl(
  questId,
  userId,
  fileName,
) {
  presignedUrlCache.delete(previewUrlCacheKey(questId, userId, fileName))
}

export function clearTravelDocumentPreviewUrlCache() {
  presignedUrlCache.clear()
}

export function isTravelDocumentImageFileName(fileName) {
  if (!fileName || typeof fileName !== 'string') return false
  const lower = fileName.toLowerCase()
  return (
    lower.includes('.jpg') ||
    lower.includes('.jpeg') ||
    lower.includes('.png') ||
    lower.includes('.gif') ||
    lower.includes('.webp') ||
    lower.includes('.bmp')
  )
}

export function isTravelDocumentPdfFileName(fileName) {
  if (!fileName || typeof fileName !== 'string') return false
  return fileName.toLowerCase().includes('.pdf')
}
